import React, { useState, useEffect } from 'react';
import { generateSmartBookingUrls } from '../utils/bookingUrls';
import './EnhancedBookingModal.css';

const EnhancedBookingModal = ({ 
  isOpen, 
  onClose, 
  bookingType, 
  game, 
  teamName,
  userLocation = '',
}) => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const bookingUrls = generateSmartBookingUrls(game, teamName, userLocation);
  const currentUrl = bookingUrls[bookingType];

  const bookingTitles = {
    tickets: '🎫 Find Game Tickets',
    hotels: '🏨 Book Hotels',
    flights: '✈️ Book Flights', 
    restaurants: '🍽️ Reserve Dining',
    rentalCars: '🚗 Rent a Car'
  };

  const bookingDescriptions = {
    tickets: `Find the best tickets for ${teamName} vs ${game.opponent} at ${game.venue}`,
    hotels: `Book your stay in ${game.venue.split(',')[0]} for game day`,
    flights: `Fly to ${game.venue.split(',')[0]} for the big game`,
    restaurants: `Make dinner reservations in ${game.venue.split(',')[0]}`,
    rentalCars: `Get a rental car for your trip to ${game.venue.split(',')[0]}`
  };

  const handleBookNow = () => {
    if (bookingType === 'restaurants') {
      // For restaurants, open Google Maps directly without showing modal
      window.open(currentUrl, '_blank', 'noopener,noreferrer');
      onClose();
    } else {
      // For other booking types, open external URL
      window.open(currentUrl, '_blank', 'noopener,noreferrer');
      onClose();
    }
  };

  const formatGameDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  return (
    <div className="enhanced-booking-modal-overlay" onClick={onClose}>
      <div 
        className={`enhanced-booking-modal ${isMobile ? 'mobile' : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="modal-header">
          <div className="modal-title-section">
            <h2>{bookingTitles[bookingType]}</h2>
            <p className="modal-description">{bookingDescriptions[bookingType]}</p>
          </div>
          <button className="modal-close-btn" onClick={onClose}>
            ✕
          </button>
        </div>

        {/* Game Info Bar */}
        <div className="game-info-bar">
          <div className="game-details">
            <span className="game-matchup">{teamName} vs {game.opponent}</span>
            <span className="game-date">{formatGameDate(game.date)}</span>
            <span className="game-venue">{game.venue}</span>
          </div>
        </div>

        {/* Main Content */}
        <div className="modal-content">
          <div className="booking-preview">
            <div className="booking-icon">
              {bookingType === 'tickets' && '🎫'}
              {bookingType === 'hotels' && '🏨'}
              {bookingType === 'flights' && '✈️'}
              {bookingType === 'restaurants' && '🍽️'}
              {bookingType === 'rentalCars' && '🚗'}
            </div>
            
            <div className="booking-details">
              <h3>Ready to Book!</h3>
              <p>We've pre-filled the search with your game details:</p>
              
              <div className="search-details">
                <div className="detail-item">
                  <span className="detail-label">Event:</span>
                  <span className="detail-value">{teamName} vs {game.opponent}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Date:</span>
                  <span className="detail-value">{formatGameDate(game.date)}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Location:</span>
                  <span className="detail-value">{game.venue}</span>
                </div>
                {bookingType === 'hotels' && (
                  <div className="detail-item">
                    <span className="detail-label">Stay:</span>
                    <span className="detail-value">Game night + 1 day</span>
                  </div>
                )}
                {bookingType === 'flights' && userLocation && (
                  <div className="detail-item">
                    <span className="detail-label">From:</span>
                    <span className="detail-value">{userLocation}</span>
                  </div>
                )}
              </div>

              <div className="booking-benefits">
                <h4>Why book through our partners:</h4>
                <ul>
                  <li>✅ Pre-filled search saves you time</li>
                  <li>✅ Trusted booking platforms</li>
                  <li>✅ Secure payment processing</li>
                  <li>✅ Customer support included</li>
                  {bookingType === 'tickets' && <li>✅ Verified authentic tickets</li>}
                  {bookingType === 'hotels' && <li>✅ Free cancellation options</li>}
                  {bookingType === 'flights' && <li>✅ Best price guarantees</li>}
                </ul>
              </div>

              <button className="book-now-btn" onClick={handleBookNow}>
                🚀 Book Now - Search Pre-Filled!
              </button>
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="modal-footer">
          <div className="footer-actions">
            <button className="secondary-btn" onClick={onClose}>
              ← Back to Schedule
            </button>
            <button className="primary-btn" onClick={handleBookNow}>
              🚀 Book Now!
            </button>
          </div>
          <div className="footer-info">
            <small>🔒 Secure booking powered by trusted partners</small>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnhancedBookingModal; 